/**
 * Fan creation and management logic
 */
import { IBotData, IBotSection, IFan, IRatedFan } from '../../../models';
import {
  DEFAULT_COEFFICIENTS,
  FanCreationResult,
  FanRatingResult,
} from '../../../models/bot';
import { dispatchMutation, dispatchQuery } from '../../../utils/dispatch.utils';
import { ahuApi, fanApi } from '../../../services';
import { generateTimestamp } from '../../index';
import { roundBasedOnArguments } from '../designAHU.utils';
/**
 * Creates a fan section and fan component
 */
export async function createFanSection(
  tunnelId: number,
  casingInsulationId: number,
  casingInnerlinerId: number,
  floorInnerlinerId: number,
  allSections: any[],
  fanIndex: number,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<FanCreationResult> {
  const nextSection = allSections[fanIndex + 1];
  const first = fanIndex === 0;
  const last = fanIndex === allSections.length - 1;
  // Create fan section
  const fanSectionData: any = await dispatchMutation(
    ahuApi.endpoints.createSection,
    {
      body: {
        length: 12,
        sitting_on_id: null,
        split: false,
        tag: '',
        tunnel_id: tunnelId,
        order_rate: 1,
        casing_insulation_id: casingInsulationId,
        casing_innerliner_id: casingInnerlinerId,
        floor_innerliner_id: floorInnerlinerId,
      },
    },
    getOptions(),
  );
  onProgress?.(DEFAULT_COEFFICIENTS.create_section); // create_section coefficient
  // Create fan
  const fanData = await dispatchMutation(
    fanApi.endpoints.createFan,
    {
      body: { section_id: fanSectionData.id },
    },
    getOptions(),
  );
  let tunnelFan = fanData;
  let tunnelFanSection = fanSectionData;
  // Update fan section length if needed
  if (nextSection && nextSection.type) {
    const section: any = await dispatchQuery(
      ahuApi.endpoints.getSection,
      fanSectionData.id,
      getOptions(),
    );
    let length = +section.required_length || 12;
    if (!first) length -= section.casing_insulation_thick || 0;
    if (!last) length -= section.casing_insulation_thick || 0;
    const isPlenum = nextSection.type === 'plenum';
    tunnelFanSection = await dispatchMutation(
      ahuApi.endpoints.updateSection,
      {
        id: fanSectionData.id,
        body: {
          auto_adjust: isPlenum,
          length: roundBasedOnArguments(length, fanSectionData.increment),
        },
      },
      getOptions(),
    );
    allSections[fanIndex].model = tunnelFanSection;
  }
  onProgress?.(DEFAULT_COEFFICIENTS.fan_create); // fan_create coefficient
  return {
    tunnelFan,
    tunnelFanSection,
    compiled: false,
  };
}
/**
 * Validates tunnel if fan already exists
 */
export async function validateTunnelIfNeeded(
  tunnelId: number,
  compiled: boolean,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<boolean> {
  if (compiled) {
    return compiled;
  }
  try {
    const error: any = await dispatchQuery(
      ahuApi.endpoints.validateTunnel,
      tunnelId,
      getOptions(),
    );
    if (error?.validation_error_messages?.errors_count) {
      onProgress?.(DEFAULT_COEFFICIENTS.compile_errors); // compile_errors coefficient
      return true;
    }
  } catch (e) {
    // Ignore validation errors for now
  }
  return false;
}
/**
 * Handles fan creation or validation
 * Note: botData should contain only the sections for the current tunnel
 */
export async function handleFanCreation(
  botData: IBotData,
  tunnelId: number,
  existingTunnelFan: any,
  existingTunnelFanSection: any,
  existingCompiled: boolean,
  casingInsulationId: number,
  casingInnerlinerId: number,
  floorInnerlinerId: number,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<FanCreationResult> {
  // Get sections from botData (should already be filtered to current tunnel)
  const allSections = botData.tunnels.flatMap((t) => t.sections);
  const fanSection = allSections.find((s) => s.type && s.type === 'fan');
  if (!fanSection) {
    return {
      tunnelFan: existingTunnelFan,
      tunnelFanSection: existingTunnelFanSection,
      compiled: existingCompiled,
    };
  }
  const fanIndex = allSections.findIndex((s) => s.type === 'fan');
  if (!existingTunnelFan || !existingTunnelFanSection) {
    const result = await createFanSection(
      tunnelId,
      casingInsulationId,
      casingInnerlinerId,
      floorInnerlinerId,
      allSections,
      fanIndex,
      getOptions,
      onProgress,
    );
    return result;
  } else {
    const compiled = await validateTunnelIfNeeded(
      tunnelId,
      existingCompiled,
      getOptions,
      onProgress,
    );
    return {
      tunnelFan: existingTunnelFan,
      tunnelFanSection: existingTunnelFanSection,
      compiled,
    };
  }
}
/**
 * Gets fan section and next section info
 */
export function getFanAndNextSection(sections: IBotSection[]): {
  fan: IBotSection | undefined;
  nextSection: IBotSection | undefined;
  first: boolean;
  last: boolean;
} {
  const validSections = sections.filter((s) => s.type && s.type.trim() !== '');
  const fanIndex = validSections.findIndex((s) => s.type === 'fan');
  const fan = fanIndex !== -1 ? validSections[fanIndex] : undefined;
  const nextSection = fanIndex !== -1 ? validSections[fanIndex + 1] : undefined;
  return {
    fan,
    nextSection,
    first: fanIndex === 0,
    last: fanIndex === validSections.length - 1,
  };
}
/**
 * Gets fan motor data for a rated fan
 */
async function getFanMotor(
  model: any,
  fanModel: any,
  fanType: 'plug' | 'plenum',
  voltageId: number,
  getOptions: () => { signal?: AbortSignal },
): Promise<any> {
  // Validate required model data
  if (!model) {
    return null;
  }

  // Validate required fanModel data
  if (!fanModel || !fanModel.id) {
    return null;
  }

  const motorParams: any = {
    mfg_id: fanType === 'plug' ? 67 : 133,
    series_id: fanType === 'plug' ? 1288 : 116,
    type_id: fanType === 'plug' ? 2 : 1,
    voltage_id: voltageId,
    rating: true,
    fan_id: fanModel.id,
    fan_type_id: fanModel.type_id,
    fan_arr_id: fanModel.arr_id,
    fan_mfg_id: fanModel.mfg_id,
    fan_series_id: fanModel.series_id,
    fan_wheel_dia_nominal_id: fanModel.wheel_dia_nominal_id,
    fan_amca_class_id: fanModel.amca_class_id,
  };
  if (fanType !== 'plug') {
    //motorParams.shaft_grounding_id = 3;
    motorParams.fan_bhp = model.bhp;
  } else {
    motorParams.housing_id = 3;
  }
  const queryParams = new URLSearchParams();
  queryParams.append('page', '1');
  Object.keys(motorParams).forEach((key) => {
    const value = motorParams[key];
    if (value !== undefined && value !== null) {
      queryParams.append(key, value.toString());
    }
  });
  const queryString = queryParams.toString();
  try {
    const result = await dispatchQuery(
      fanApi.endpoints.getFanMotors,
      queryString,
      getOptions(),
    );
    if (Array.isArray(result) && result.length > 0) {
      return (
        result.find((e) => e.enclosure === 'TEFC') ||
        result.find((e) => e.enclosure === 'ODP') ||
        result[0]
      );
    }
    return null;
  } catch (e) {
    return null;
  }
}
/**
 * Gets fan data from rated model
 */
async function getFanData(
  model: any,
  getOptions: () => { signal?: AbortSignal },
): Promise<any> {
  const spn = model.spn || model.model;
  if (!spn) return {};
  try {
    const result = await dispatchQuery(
      fanApi.endpoints.getFanModel,
      `?&spn=${spn}`,
      getOptions(),
    );
    if (Array.isArray(result) && result.length > 0) {
      return result[0];
    }
    return {};
  } catch (e) {
    return {};
  }
}
async function getFanWithMotor(
  models: IRatedFan[],
  botData: IBotData,
  type: 'plug' | 'plenum',
  getOptions: () => { signal?: AbortSignal },
) {
  for (let i = 0; i < Math.min(models.length, 3); i++) {
    const model = models[i];
    if (!model) continue;

    const fan = await getFanData(model, getOptions);
    if (!fan) continue;

    const motor = await getFanMotor(
      model,
      fan,
      type,
      botData.voltage_id || 1,
      getOptions,
    );

    if (motor) {
      return { fan, motor, model };
    }
  }

  return null;
}

function collectPlenumFanData(mfg = 'nyblower', series = '') {
  if (mfg === 'nyblower')
    return {
      manufacturer: mfg,
      series: series || 'FA',
      specified_deflection: 1.5,
      other: {
        afs_sensor_id: null,
        afs_xmitter_config: null,
        cube_housing: null,
        outlet_guard_id: null,
        power: null,
        protective_cage_id: null,
        enclosure: null,
        inlet_guard_id: null,
        style: null,
        wheel_dia_nominal: null,
        wheel_type: null,
        motor_position: null,
        motor_speed_nominal: null,
        efficiency: 'premium',
        fan_spacing_sides: 'evenly_spaced',
        fan_spacing_top_btm: 'evenly_spaced',
        max_peak_pressure_percent: 90,
        motor_mfg: 'Baldor',
        mtr_controller_id: 'vfd',
        wheel_mtl: 'aluminum',
        isp: 0,
        esp: 0,
      },
    };
  if (mfg === 'comefri')
    return {
      manufacturer: mfg,
      series: 'ANPA PW',
      specified_deflection: 1,
      other: {},
    };

  return {
    manufacturer: mfg,
    series: 'EPFN',
    specified_deflection: 1,
    other: {},
  };
}

/**
 * Rates a plug fan
 */
async function ratePlugFan(
  section: IBotSection,
  botData: IBotData,
  ahuParams: any,
  getOptions: () => { signal?: AbortSignal },
): Promise<FanRatingResult> {
  const qty = (section.wide || 1) * (section.high || 1);
  const sa_volume_max = +(section.airflow || 0);
  const tsp = section.tsp || 0;
  // Get voltage text from botData or default
  const voltageText = botData.voltage_name; // Default, should come from botData
  const data = {
    part: 'fan',
    manufacturer: 'ziehl_abegg',
    type: 'plug',
    input: {
      elevation: 0,
      type: 'plug',
      cube_housing: null,
      series: 'ZAbluefin-ECblue',
      style: '--',
      arr: 4,
      wheel_mtl: 'zamid',
      rotation: 'cw',
      amca_class: null,
      qty_wide: section.wide || 1,
      qty_high: section.high || 1,
      airflow_per_fan: sa_volume_max / qty,
      airflow_actual: sa_volume_max,
      tsp: +tsp,
      fan_inlet_pressure: 0,
      fan_inlet_temp: 70,
      redundancy: 'none',
      speed_condition: null,
      mounting: 'horizontal',
      orientation: 3,
      housed_discharge: null,
      assy_height_from_floor: 0,
      assy_distance_from_ceiling: 0,
      assy_distance_from_section_ups: 0,
      esp: 2,
      bulkhead_wall_to_ahu_side_wall: 0,
      motor_type: 'ec',
      motor_mfg: 'Ziehl-Abegg',
      mtr_series: 'ECblue',
      motor_voltage: voltageText,
      shaft_grounding: null,
      qty,
      aw_width: ahuParams?.current_width || 0,
      aw_height: ahuParams?.current_height || 0,
      air_stream_id: section.air_stream_id,
    },
    function_name: 'SEARCH_PRODUCTS',
  };
  try {
    const rate: any = await dispatchMutation(
      ahuApi.endpoints.rate,
      { body: data },
      getOptions(),
    );
    //@ts-ignore
    const models = [...(rate?.models || [])]?.sort(
      (a, b) => +a.bp_kw - +b.bp_kw,
    );

    if (models?.length) {
      const {
        fan: fan_model,
        model,
        motor,
      } = (await getFanWithMotor(models, botData, 'plug', getOptions)) || {};
      if (!fan_model)
        return {
          model: null,
          error: 'No suitable fan available',
        };
      const curve_data = {
        manufacturer: 'ziehl_abegg',
        part: 'fan',
        type: 'plug',
        input: {
          mpn: model?.mpn,
          qty_wide: section.wide || 1,
          qty_high: section.high || 1,
          airflow_per_fan: sa_volume_max / qty,
          airflow_actual: sa_volume_max,
          tsp: +tsp,
          elevation: 0,
          qty,
        },
        function_name: 'GET_CURVE',
      };
      const resp: any = await dispatchMutation(
        ahuApi.endpoints.rate,
        { body: curve_data },
        getOptions(),
      );
      if (resp.models?.length) {
        if (!motor) {
          return {
            model: model,
            curve_model: resp.models[0],
            error: 'No suitable motor available',
            fan_model,
            input: data.input,
          };
        }
        return {
          model: model,
          curve_model: resp.models[0],
          fan_model,
          motor,
          input: data.input,
          error: null,
        };
      }
    }
    return {
      model: null,
      error: 'No suitable fan available',
    };
  } catch (e: any) {
    if (e.statusText !== 'abort') {
      return {
        model: null,
        error: 'No suitable fan available',
      };
    }
    throw e;
  }
}
/**
 * Rates a plenum fan
 */
async function ratePlenumFan(
  section: IBotSection,
  botData: IBotData,
  getOptions: () => { signal?: AbortSignal },
): Promise<FanRatingResult> {
  const qty = (section.wide || 1) * (section.high || 1);
  const sa_volume_max = +(section.airflow || 0);
  const tsp = section.tsp || 0;
  const voltageText = botData.voltage_name;
  const configs = [
    collectPlenumFanData('nyblower'),
    collectPlenumFanData('nyblower'),
    collectPlenumFanData('nyblower', 'EZ'),
    collectPlenumFanData('nyblower', 'EZ'),
    collectPlenumFanData('comefri'),
    collectPlenumFanData('twin_city_fan'),
  ];

  for (const { manufacturer, series, specified_deflection, other } of configs) {
    const data = {
      part: 'fan',
      manufacturer,
      type: 'plenum',
      input: {
        elevation: 0,
        type: 'plenum',
        series,
        wheel_width_percent: null,
        arr: 4,
        rotation: 'cw',
        amca_class: null,
        qty_wide: section.wide || 1,
        qty_high: section.high || 1,
        airflow_per_fan: sa_volume_max / qty,
        airflow_actual: sa_volume_max,
        tsp: +tsp,
        fan_inlet_pressure: 0,
        fan_inlet_temp: 70,
        redundancy: 'none',
        speed_condition: 'any_speed',
        mounting: 'horizontal',
        orientation: 3,
        housed_discharge: null,
        assy_height_from_floor: 0,
        assy_distance_from_ceiling: 0,
        assy_distance_from_section_ups: 0,
        bulkhead_wall_to_ahu_side_wall: 0,
        motor_type: 'ac',
        frequency: '60',
        shaft_grounding: null,
        specified_deflection,
        mounting_method: 'direct_mount',
        qty,
        air_stream_id: section.air_stream_id,
        voltage_id: voltageText,
        ...other,
      },
    };
    if (manufacturer === 'comefri') {
      //@ts-ignore
      data.function_name = 'fan_rate';
    }

    try {
      const resp: any = await dispatchMutation(
        ahuApi.endpoints.rate,
        { body: data },
        getOptions(),
      );

      if (!resp.models?.length) continue;

      const models = [...resp.models].sort((a, b) => a.bhp - b.bhp);

      const {
        fan: fan_model,
        model,
        motor,
      } = (await getFanWithMotor(models, botData, 'plenum', getOptions)) || {};

      if (!motor) continue;
      let curves;
      if (manufacturer === 'comefri') {
        const rate_data = {
          part: 'fan',
          manufacturer,
          function_name: 'get_curve_data',
          type: 'plenum',
          input: {
            ...data.input,
            ...model,
          },
        };

        const incoming_data: any = await dispatchMutation(
          ahuApi.endpoints.rate,
          { body: rate_data },
          getOptions(),
        );

        curves = {
          rpm_curve: incoming_data.data.rpm_curve,
          system_curve: incoming_data.data.system_curve,
          ...incoming_data.data.models[0],
        };
      }
      // ✅ success — stop loop
      return {
        model: curves || model,
        fan_model,
        motor,
        error: null,
        input: data.input,
        curves,
      };
    } catch (e: any) {
      if (e.statusText === 'abort') throw e;
      // otherwise try next config
    }
  }

  // ❌ all 4 attempts failed
  return {
    model: null,
    error: 'No suitable fan available',
  };
}
/**
 * Rates a fan based on section type_id
 * Defaults to plenum if type_id is not specified
 */
export async function rateFan(
  section: IBotSection,
  botData: IBotData,
  ahuParams: any,
  getOptions: () => { signal?: AbortSignal },
): Promise<FanRatingResult> {
  // type_id 4 = plug fan, otherwise plenum
  const fanTypeId = section.type_id;
  if (fanTypeId === 2) {
    return await ratePlugFan(section, botData, ahuParams, getOptions);
  } else {
    return await ratePlenumFan(section, botData, getOptions);
  }
}
/**
 * Creates a fan with rated data (matches Backbone create_fan function)
 * 1. Creates the fan via POST
 * 2. Creates curves with bhp, sp, system_curve
 * 3. Updates the fan with all rated data via PATCH
 */
export async function createFanWithRatedData(
  sectionId: number,
  ratingData: FanRatingResult,
  voltageId: number,
  getOptions: () => { signal?: AbortSignal },
): Promise<any> {
  const { model, curve_model, fan_model, motor, input } = ratingData;
  if (!model || !fan_model || !motor || !input) {
    throw new Error('Missing required fan rating data');
  }
  // Step 1: Create the fan
  const fan: IFan = await dispatchMutation(
    fanApi.endpoints.createFan,
    {
      body: { section_id: sectionId },
    },
    getOptions(),
  );
  // Step 2: Create curves
  const curvesData: any = {
    id: fan.id,
    bhp: JSON.stringify(model.bhp_curve || curve_model?.bhp_curve || []),
    cfm: '', // CFM points not used in bot
    sp: JSON.stringify(
      model.rpm_curve ||
        curve_model?.rpm_curve ||
        curve_model?.MAX?.CHART ||
        [],
    ),
    system_curve: JSON.stringify(
      model.system_curve || curve_model?.system_curve || [],
    ),
  };
  await dispatchMutation(
    fanApi.endpoints.updateFanCurve,
    {
      id: fan.id,
      body: curvesData,
    },
    getOptions(),
  );
  // Step 3: Prepare fan update data (following Backbone create_fan pattern)
  const fanUpdateData: any = { ...model };
  // Delete fields that shouldn't be sent
  delete fanUpdateData.amca_class;
  delete fanUpdateData.arr_id;
  delete fanUpdateData.density;
  delete fanUpdateData.discharge_vel;
  delete fanUpdateData.rotation;
  delete fanUpdateData.section_id;
  delete fanUpdateData.series;
  delete fanUpdateData.wheel_dia_percent;
  delete fanUpdateData.wheel_width_percent;
  delete fanUpdateData.wheel_size;
  delete fanUpdateData.bhp_curve;
  delete fanUpdateData.rpm_curve;
  delete fanUpdateData.system_curve;
  // Handle plug fan (fan_type_id == 2) vs plenum fan
  // Check model.fan_type_id first (matches Backbone pattern), fallback to fan_model.fan_type_id
  const fanTypeId = model.fan_type_id ?? fan_model.fan_type_id;
  if (fanTypeId === 2) {
    // Plug fan specific handling
    delete fanUpdateData.style;
    delete fanUpdateData.fan_amca_class_id;
    delete fanUpdateData.motor_power_total;
    delete fanUpdateData.bp_kw_total;
    delete fanUpdateData.wheel_mtl_id;
    delete fanUpdateData.wheel_mtl;
    delete fanUpdateData.mtr_supplied_by_id;
    fanUpdateData.MAX = curve_model?.BP || curve_model?.MAX;
    fanUpdateData.system_effect_id = null;
    fanUpdateData.speed_condition_id = null;
    fanUpdateData.mtr_supplied_by_id = null;
    fanUpdateData.si_params = JSON.stringify({
      series_id: null,
      mfg_id: null,
      isolator_type_id: null,
    });
    fanUpdateData.rating_motor_fla = fanUpdateData.fla;
    fanUpdateData.discharge_id = 2;
  } else {
    // Plenum fan specific handling
    fanUpdateData.si_params = JSON.stringify({
      series_id: '176',
      mfg_id: '99',
      isolator_type_id: '1',
    });
    fanUpdateData.si_specified_deflection_id = 19;
    fanUpdateData.mounting_method_id = 3;
    fanUpdateData.motor_power = null;
  }
  const airflow_actual = +model.airflow_actual || +input.airflow_actual;
  const tsp = model.tsp || input.tsp;
  // Add common fields
  fanUpdateData.rated_fan_speed_max =
    fanUpdateData.speed_max || model.speed_max;
  fanUpdateData.fan_params = JSON.stringify(fan_model);
  fanUpdateData.master_fan_id = fan_model.id;
  fanUpdateData.motor_params = JSON.stringify(motor);
  fanUpdateData.master_motor_id = motor.id;
  fanUpdateData.fan_qty = input.qty;
  fanUpdateData.params_motor = motor;
  fanUpdateData.tsp = tsp;
  fanUpdateData.airflow_per_fan = airflow_actual / input.qty;
  fanUpdateData.airflow_actual = airflow_actual;
  fanUpdateData.qty_wide = input.qty_wide;
  fanUpdateData.qty_high = input.qty_high;
  fanUpdateData.motor_voltage_id = voltageId;
  fanUpdateData.air_stream_id = input.air_stream_id;

  fanUpdateData.rating_datetime = generateTimestamp();
  // Step 4: Update the fan with all rated data
  const updatedFan: any = await dispatchMutation(
    fanApi.endpoints.updateFan,
    {
      id: fan.id,
      body: fanUpdateData,
    },
    getOptions(),
  );
  // Add min clearances to the returned fan object
  // Create a new object since updatedFan may not be extensible
  return {
    ...updatedFan,
    min_clear_ups: fan_model.min_clear_upstream || 0,
    min_clear_downs: fan_model.min_clear_downstream || 0,
  };
}
